import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import EnlargedPreviewCard, {
  ENLARGED_IMAGE_SIZE_PX,
  fitPreview,
  viewportMaxPreviewHeight,
} from './EnlargedPreviewCard';

import type { Dispatch, SetStateAction } from 'react';

type RenderProp = {
  enlargedState: DOMRect | null;
  setEnlargedState: Dispatch<SetStateAction<DOMRect | null>>;
  renderEnlargedImage: () => React.ReactNode;
};

export default function EnlargedImageRenderProp({
  children,
  src,
  smallPhotoHeight,
  enlargedPhotoHeight = 345,
  direction = 'left',
  isVideo = false,
  isLogo = false,
  darkBackground = false,
}: {
  children: (r: RenderProp) => React.ReactElement;
  src: string;
  /**
   * Height of the thumbnail the preview is anchored to, used to centre the
   * card on it. Omit when the thumbnail is sized by aspect ratio rather than a
   * fixed height — the measured anchor rect is then the only correct value.
   */
  smallPhotoHeight?: number;
  enlargedPhotoHeight?: number;
  direction?: 'left' | 'right';
  isVideo?: boolean;
  isLogo?: boolean;
  darkBackground?: boolean;
}) {
  const [enlargedState, setEnlargedState] = useState<DOMRect | null>(null);
  const [enlargedSize, setEnlargedSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const maxPreviewHeight = Math.min(
    enlargedPhotoHeight,
    ENLARGED_IMAGE_SIZE_PX,
    viewportMaxPreviewHeight(),
  );

  useEffect(() => {
    if (enlargedState == null) {
      setEnlargedSize(null);
    }
  }, [enlargedState]);

  useEffect(() => {
    setEnlargedState(null);
  }, [src]);

  const renderEnlargedImage = () => {
    return (
      <EnlargedPreviewCard
        anchorRect={enlargedState!}
        anchorHeight={smallPhotoHeight ?? enlargedState?.height ?? 0}
        width={enlargedSize?.width ?? 0}
        height={enlargedSize?.height ?? maxPreviewHeight}
        direction={direction}
        isVisible={!!enlargedSize}
        darkBackground={darkBackground || isLogo}
      >
        {isVideo ? (
          <EnlargedVideo
            src={src}
            controls={false}
            onLoadedMetadata={e => {
              const { videoWidth, videoHeight } = e.currentTarget;
              setEnlargedSize(
                fitPreview(videoWidth, videoHeight, maxPreviewHeight),
              );
            }}
          />
        ) : (
          <EnlargedImage
            src={src}
            onLoad={e => {
              const { naturalWidth, naturalHeight } = e.currentTarget;
              setEnlargedSize(
                fitPreview(naturalWidth, naturalHeight, maxPreviewHeight),
              );
            }}
            isLogo={isLogo}
          />
        )}
      </EnlargedPreviewCard>
    );
  };

  return children({
    enlargedState,
    setEnlargedState,
    renderEnlargedImage,
  });
}

const EnlargedImage = styled.img<{
  isLogo?: boolean;
}>`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: ${props => (props.isLogo ? 'contain' : 'cover')};
`;

const EnlargedVideo = styled.video`
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
`;
